import { UPMS_CONFIG, S3_CONFIG } from "../config/UPMSConfig";

/**
 * Message types supported by UPMS
 */
export enum UPMSMessageType {
  NEW_RX = "NEW_RX",
  TRANSFER_RX = "TRANSFER_RX",
  RENEWAL_RX = "RENEWAL_RX",
}

/** Union message key for each message type. */
const MESSAGE_KEY: Record<UPMSMessageType, string> = {
  [UPMSMessageType.NEW_RX]: "newPrescriptionMessage",
  [UPMSMessageType.TRANSFER_RX]: "transferInPrescriptionMessage",
  [UPMSMessageType.RENEWAL_RX]: "renewalPrescriptionMessage",
};

/** Message types that carry a metadata.sourceFileUrl (UPMS re-reads the payload from S3). */
const REQUIRES_SOURCE_FILE_URL: ReadonlySet<UPMSMessageType> = new Set([
  UPMSMessageType.NEW_RX,
  UPMSMessageType.RENEWAL_RX,
]);

/**
 * Utility class to transform prescription data to UPMS format
 */
export class UPMSTransformer {
  /**
   * Main transformation method
   * @param prescriptionData - The prescription data from S3
   * @param sourceFileName - The S3 filename for metadata
   * @param messageType - The type of prescription message (defaults to NEW_RX)
   */
  static transformToUPMS(
    prescriptionData: any,
    sourceFileName: string,
    messageType: UPMSMessageType = UPMSMessageType.NEW_RX
  ): any {
    const messageKey = MESSAGE_KEY[messageType];

    const messageContent: any = {
      body: this.transformBodyForType(prescriptionData, messageType),
      header: this.transformHeaderForType(prescriptionData, messageType)
    };

    // NewRx and Renewal carry a sourceFileUrl — UPMS re-reads the payload from S3.
    if (REQUIRES_SOURCE_FILE_URL.has(messageType)) {
      messageContent.metadata = {
        sourceFileUrl: S3_CONFIG.getFileUrl(sourceFileName)
      };
    }

    const envelope: any = {
      senderId: UPMS_CONFIG.SENDER_ID,
      clientToken: this.generateClientToken(),
      transmissionChannel: this.getTransmissionChannel(messageType),
      message: {
        [messageKey]: messageContent
      }
    };

    // Renewal responses ride the eScribe/SureScripts network.
    if (messageType === UPMSMessageType.RENEWAL_RX) {
      envelope.escribeNetwork = UPMS_CONFIG.ESCRIBE_NETWORK;
    }

    return envelope;
  }

  /** Select the body builder for the given message type. */
  private static transformBodyForType(data: any, messageType: UPMSMessageType): any {
    switch (messageType) {
      case UPMSMessageType.TRANSFER_RX:
        return this.transformTransferRxBody(data);
      case UPMSMessageType.RENEWAL_RX:
        // Renewal payloads are already built in UPMS (camelCase) shape — pass through.
        return this.transformRenewalRxBody(data);
      default:
        return this.transformBody(data);
    }
  }

  /** Select the header builder for the given message type. */
  private static transformHeaderForType(data: any, messageType: UPMSMessageType): any {
    // Renewal carries its own fully-built header (with relatesToMessageId / rxReferenceNumber);
    // pass it through rather than re-deriving via transformHeader (which hardcodes FROM_IDENTIFIER).
    if (messageType === UPMSMessageType.RENEWAL_RX) {
      return data.header;
    }
    return this.transformHeader(data);
  }

  /**
   * Transform the body section for RenewalRx.
   * Like TransferRx, the renewal payload is already in camelCase UPMS shape, so pass it through.
   */
  private static transformRenewalRxBody(data: any): any {
    return {
      response: data.response,
      patient: data.patient,
      pharmacy: data.pharmacy,
      prescriber: data.prescriber,
      medicationResponse: data.medicationResponse,
    };
  }

  /**
   * Transform the body section for NewRx
   */
  private static transformBody(data: any): any {
    return {
      medicationPrescribed: this.transformMedication(data.MedicationPrescribed),
      patient: this.transformPatient(data.Patient),
      pharmacy: this.transformPharmacy(data.Pharmacy),
      prescriber: this.transformPrescriber(data.Prescriber),
      prohibitRenewalRequest: false
    };
  }

  /**
   * Transform the body section for TransferRx
   * For TransferRx, the data is already in camelCase format, so just pass it through
   */
  private static transformTransferRxBody(data: any): any {
    return {
      patient: data.patient,
      prescriber: data.prescriber,
      pharmacist: data.pharmacist,
      pharmacy: data.pharmacy,
      sourcePharmacy: data.sourcePharmacy,
      medicationPrescribed: data.medicationPrescribed,
      medicationTransferred: data.medicationTransferred
    };
  }

  /**
   * Transform medication data
   */
  private static transformMedication(med: any): any {
    if (!med) return {};

    return {
      daysSupply: med.DaysSupply || "30",
      drugDescription: med.DrugDescription,
      numberOfRefills: med.NumberOfRefills || "5",
      drugCoded: {
        productCode: {
          code: med.NDC || "",
          qualifier: "ND"
        },
        strength: {
          strengthValue: `${med.Strength?.Value} ${med.Strength?.UnitOfMeasureDescription}`,
          strengthForm: {
            code: med.Strength?.FormCode || "C25158"
          },
          strengthUnitOfMeasure: {
            code: med.Strength?.UnitOfMeasureCode || "C28253"
          }
        }
      },
      quantity: {
        codeListQualifier: med.Quantity?.QualifierCode || "38",
        quantityUnitOfMeasure: { code: med.Quantity?.UnitOfMeasureCode || "C48480" },
        value: med.Quantity?.Value || "30.0"
      },
      sig: {
        sigText: med.SigText || "Take 1 tablet by mouth daily."
      },
      ...(med.Note && { note: med.Note }),
      substitutions: med.DispenseAsWritten?.toString() || "0",
      writtenDate: {
        date: med.WrittenDate?.Date || new Date().toISOString().split('T')[0]
      }
    };
  }

  /**
   * Transform patient data
   */
  private static transformPatient(patient: any): any {
    if (!patient) return {};

    return {
      humanPatient: {
        address: {
          addressLine1: patient.Address?.AddressLine1 || "",
          ...(patient.Address?.AddressLine2 && { addressLine2: patient.Address.AddressLine2 }),
          city: patient.Address?.City || "",
          stateProvince: patient.Address?.State || "",
          postalCode: patient.Address?.ZipCode || "",
          countryCode: patient.Address?.CountryCode || "US"
        },
        communicationNumbers: {
          primaryTelephone: {
            number: patient.CommunicationNumbers?.PrimaryTelephone?.Number || ""
          }
        },
        dateOfBirth: {
          date: patient.DateOfBirth?.Date || ""
        },
        gender: patient.Gender || "U",
        name: {
          firstName: patient.Name?.FirstName || "",
          lastName: patient.Name?.LastName || ""
        }
      }
    };
  }

  /**
   * Transform pharmacy data (returns constant Amazon Pharmacy details)
   */
  private static transformPharmacy(pharmacy: any): any {
    // Pharmacy info is constant, just return the config object
    return UPMS_CONFIG.PHARMACY;
  }

  /**
   * Transform prescriber data to UPMS format
   */
  private static transformPrescriber(prescriber: any): any {
    if (!prescriber) return {};

    // Build identification object
    const identification: any = {
      npi: prescriber.Identification?.NPI || "",
      deaNumber: prescriber.Identification?.DEANumber || ""
    };

    // Add stateLicenseNumber if available (optional field)
    if (prescriber.Identification?.StateLicenseNumber) {
      identification.stateLicenseNumber = prescriber.Identification.StateLicenseNumber;
    }

    // Build communication numbers - handle both nested and flat structures
    const communicationNumbers: any = {
      primaryTelephone: {
        number: prescriber.CommunicationNumbers?.PrimaryTelephone?.Number || ""
      }
    };

    // Add fax if available - transform Number to number for UPMS
    if (prescriber.CommunicationNumbers?.Fax && prescriber.CommunicationNumbers.Fax.length > 0) {
      communicationNumbers.fax = prescriber.CommunicationNumbers.Fax.map((f: any) => ({
        number: f.Number
      }));
    }

    const address: any = {
      stateProvince: prescriber.Address?.State || "",
      addressLine1: prescriber.Address?.AddressLine1 || "",
      countryCode: prescriber.Address?.CountryCode || "US",
      city: prescriber.Address?.City || "",
      postalCode: prescriber.Address?.ZipCode || ""
    };

    // Only add addressLine2 if it exists
    if (prescriber.Address?.AddressLine2) {
      address.addressLine2 = prescriber.Address.AddressLine2;
    }

    return {
      nonVeterinarian: {
        identification,
        placeOfService: prescriber.PlaceOfService || "11",
        communicationNumbers,
        name: {
          lastName: prescriber.Name?.LastName || "",
          firstName: prescriber.Name?.FirstName || ""
        },
        address,
        ...(prescriber.PracticeLocation?.ClinicName && {
          practiceLocation: { businessName: prescriber.PracticeLocation.ClinicName }
        })
      }
    };
  }

  /**
   * Transform header data
   * Handles both PascalCase (from S3/NewRx) and camelCase (from UI/TransferRx)
   */
  private static transformHeader(data: any): any {
    const header = data.Header || data.header || {};

    return {
      messageId: header.MessageID || header.messageID || UPMS_CONFIG.SENDER_ID,
      sentTime: header.SentTime || header.sentTime || new Date().toISOString(),
      from: {
        identifier: UPMS_CONFIG.FROM_IDENTIFIER,
        qualifier: UPMS_CONFIG.FROM_QUALIFIER
      },
      to: {
        identifier: header.To || header.to || UPMS_CONFIG.PHARMACY.identification.ncpdpId,
        qualifier: UPMS_CONFIG.TO_QUALIFIER
      },
      ...(( header.PrescriberOrderNumber || header.prescriberOrderNumber) && {
        prescriberOrderNumber: header.PrescriberOrderNumber || header.prescriberOrderNumber
      }),
    };
  }

  /**
   * Get the transmission channel for a given message type
   */
  private static getTransmissionChannel(messageType: UPMSMessageType): string {
    switch (messageType) {
      case UPMSMessageType.NEW_RX:
        return UPMS_CONFIG.TRANSMISSION_CHANNELS.NEW_RX;
      case UPMSMessageType.TRANSFER_RX:
        return UPMS_CONFIG.TRANSMISSION_CHANNELS.TRANSFER_RX;
      case UPMSMessageType.RENEWAL_RX:
        return UPMS_CONFIG.TRANSMISSION_CHANNELS.RENEWAL_RX;
      default:
        return UPMS_CONFIG.TRANSMISSION_CHANNELS.NEW_RX;
    }
  }

  /**
   * Generate a unique client token for each request
   * Format: QA_TESTING_<timestamp>_<random>
   */
  private static generateClientToken(): string {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    return `APAQ_QA_TESTING_${timestamp}_${random}`;
  }
}
